import React, {useCallback, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useDropzone} from 'react-dropzone';
import axios from 'axios';
import {addFile, updateFile, deleteFile} from '@form/actions/FileActions';
import {modalHide} from '@core/actions/ModalActions';

function bytesToSize(bytes) {
    if (bytes === 0) return '0 Bt';
    const k = 1024;
    const sizes = ['Bt', 'Kb', 'Mb', 'Gb'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getExtension(filename) {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

export default function FileUploadModal({fileDependencies}) {
    const dispatch = useDispatch();
    const files = useSelector(state => state.file.data);
    const isOpen = useSelector(state => state.modal.fileUpload);
    const [showAllFormats, setShowAllFormats] = useState(false);

    const limitCount = fileDependencies?.limitFiles?.resume || 20;
    const limitSize = fileDependencies?.sizeMax || 0;
    const minSize = fileDependencies?.sizeMin || 0;
    const allowedExtensions = Array.from(new Set([
        ...(fileDependencies?.allowedExtensions || []),
        ...(fileDependencies?.allowedMediaExtensions || []),
        ...(fileDependencies?.needConvertFormats || []),
    ].map(e => String(e).toLowerCase()))).sort();
    const commonExtensions = (fileDependencies?.commonExtensions || []).map(e => String(e).toLowerCase());

    const validateFile = (file) => {
        const ext = getExtension(file.name);
        if (!ext) return 'File Format is empty';
        if (!allowedExtensions.includes(ext)) return 'Unsupported File Format';
        if (limitSize && file.size > limitSize) return `File is bigger than ${bytesToSize(limitSize)}`;
        if (file.size === minSize) return 'File is empty';
        if (files.length >= limitCount) return `Max Number of Files is ${limitCount}`;

        const duplicate = files.some(f => f.nameFile === file.name && f.size === file.size);
        if (duplicate) return 'File uploaded already';

        return null;
    };

    const uploadFile = async (file) => {
        const uid = file.name + '_' + Date.now() + '_' + Math.random();
        const fileData = {
            uid,
            name: file.name,
            nameFile: file.name,
            originalName: file.name,
            size: file.size,
            uploading: true,
            status: true,
            error: '',
            loading: 0,
        };

        const error = validateFile(file);
        if (error) {
            dispatch(addFile({...fileData, status: false, uploading: false, error}));
            return;
        }

        dispatch(addFile(fileData));

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post('/api/file/', formData, {
                onUploadProgress: (e) => {
                    const pct = Math.round((e.loaded / e.total) * 100);
                    dispatch(updateFile({...fileData, loading: pct}));
                },
            });

            if (response.data.error) {
                dispatch(updateFile({...fileData, uploading: false, status: false, error: response.data.error, loading: 100}));
            } else {
                dispatch(updateFile({
                    ...fileData,
                    uploading: false,
                    loading: 100,
                    name: response.data.name,
                    originalName: response.data.originalName,
                    mimeType: response.data.mimeType,
                }));
            }
        } catch {
            dispatch(updateFile({...fileData, uploading: false, status: false, error: 'Connection error', loading: 100}));
        }
    };

    // Surface rejected files as failed entries instead of silently dropping
    // them — the native picker filters by `accept`, but a user can still
    // drag-drop anything onto the zone, and a missing file is more
    // confusing than a clear "Unsupported File Format" row.
    const onDrop = useCallback((acceptedFiles, rejectedFiles = []) => {
        acceptedFiles.forEach(uploadFile);
        rejectedFiles.forEach(file => {
            dispatch(addFile({
                uid: file.name + '_' + Date.now() + '_' + Math.random(),
                name: file.name,
                nameFile: file.name,
                originalName: file.name,
                size: file.size,
                uploading: false,
                status: false,
                error: 'Unsupported File Format',
                loading: 0,
            }));
        });
    }, [dispatch, uploadFile]);

    // `accept` filters the OS file dialog cross-platform (Linux/Mac/Windows)
    // to the same extension list `validateFile()` enforces — keeps the two
    // gates consistent and removes the "I can pick any file but the modal
    // rejects it" friction.
    const acceptString = allowedExtensions.length > 0
        ? allowedExtensions.map(ext => '.' + ext).join(',')
        : undefined;

    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        onDrop,
        multiple: true,
        noClick: true,
        accept: acceptString,
    });

    const close = () => dispatch(modalHide());

    if (!isOpen) return null;

    const successFiles = files.filter(f => f.status);

    return (
        <div className="resume-upload-overlay" onClick={e => { if (e.target === e.currentTarget) close(); }}>
            <div className="resume-upload">
                {/* Header */}
                <button className="resume-upload__close" onClick={close} type="button" aria-label="Close">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>

                <div className="resume-upload__header">
                    <div className="resume-upload__header-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                    </div>
                    <div className="resume-upload__header-text">
                        <h2 className="resume-upload__title">Upload Materials</h2>
                        <p className="resume-upload__subtitle">Add your old resume, references, or any supporting documents</p>
                    </div>
                </div>

                {/* Dropzone */}
                <div className="resume-upload__body">
                    <div {...getRootProps()} className={`resume-upload__dropzone ${isDragActive ? 'resume-upload__dropzone--active' : ''}`}>
                        <input {...getInputProps()} />

                        {files.length === 0 ? (
                            <div className="resume-upload__dropzone-empty">
                                <div className="resume-upload__dropzone-icon">
                                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="17 8 12 3 7 8" />
                                        <line x1="12" y1="3" x2="12" y2="15" />
                                    </svg>
                                </div>
                                <div className="resume-upload__dropzone-title">
                                    {isDragActive ? 'Drop files here' : 'Drag and drop files here'}
                                </div>
                                <div className="resume-upload__dropzone-or">or</div>
                                <button className="resume-upload__browse" type="button" onClick={open}>
                                    Browse Files
                                </button>
                                <div className="resume-upload__dropzone-limits">
                                    Max {limitCount} files, up to {bytesToSize(limitSize)} each
                                </div>
                            </div>
                        ) : (
                            <div className="resume-upload__dropzone-compact">
                                <span className="resume-upload__dropzone-hint">
                                    {isDragActive ? 'Drop to add more' : 'Drop files here or'}
                                </span>
                                <button className="resume-upload__browse resume-upload__browse--sm" type="button" onClick={open}>
                                    Add More
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Supported formats */}
                    {files.length === 0 && allowedExtensions.length > 0 && (() => {
                        const commonShown = commonExtensions.filter(ext => allowedExtensions.includes(ext));
                        const rest = allowedExtensions.filter(ext => !commonShown.includes(ext));
                        const hasCommon = commonShown.length > 0;
                        const hasRest = rest.length > 0;
                        const visibleExts = !hasCommon
                            ? allowedExtensions
                            : (showAllFormats ? [...commonShown, ...rest] : commonShown);
                        const showToggle = hasCommon && hasRest;

                        return (
                            <div className="resume-upload__formats">
                                <div className="resume-upload__formats-label">Supported formats</div>
                                <div className="resume-upload__formats-list">
                                    {visibleExts.map(ext => (
                                        <span key={ext} className="resume-upload__formats-chip">.{ext}</span>
                                    ))}
                                </div>
                                {showToggle && (
                                    <button
                                        type="button"
                                        className={`resume-upload__formats-toggle ${showAllFormats ? 'resume-upload__formats-toggle--open' : ''}`}
                                        onClick={() => setShowAllFormats(v => !v)}
                                    >
                                        <span>
                                            {showAllFormats ? 'Show less' : `Show ${rest.length} more formats`}
                                        </span>
                                        <svg
                                            width="12" height="12" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2.5"
                                            strokeLinecap="round" strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        );
                    })()}

                    {/* File list */}
                    {files.length > 0 && (
                        <div className="resume-upload__files">
                            {files.map(file => (
                                <div key={file.uid} className={`resume-upload__file ${!file.status ? 'resume-upload__file--error' : ''}`}>
                                    {file.status ? (
                                        <>
                                            <div className="resume-upload__file-icon">
                                                {file.uploading ? (
                                                    <div className="resume-account__spinner" style={{width: 18, height: 18, borderWidth: 2}} />
                                                ) : (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                )}
                                            </div>
                                            <div className="resume-upload__file-info">
                                                <div className="resume-upload__file-name">
                                                    {file.uploading ? file.nameFile : file.originalName}
                                                </div>
                                                <div className="resume-upload__file-size">{bytesToSize(file.size)}</div>
                                            </div>
                                            {file.uploading && (
                                                <div className="resume-upload__file-bar">
                                                    <div className="resume-upload__file-bar-fill" style={{width: `${file.loading}%`}} />
                                                </div>
                                            )}
                                            {!file.uploading && (
                                                <button className="resume-upload__file-remove" type="button" onClick={() => dispatch(deleteFile(file))}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="resume-upload__file-icon resume-upload__file-icon--error">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="15" y1="9" x2="9" y2="15" />
                                                    <line x1="9" y1="9" x2="15" y2="15" />
                                                </svg>
                                            </div>
                                            <div className="resume-upload__file-info">
                                                <div className="resume-upload__file-name">{file.nameFile}</div>
                                                <div className="resume-upload__file-error">{file.error}</div>
                                            </div>
                                            <button className="resume-upload__file-remove" type="button" onClick={() => dispatch(deleteFile(file))}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {successFiles.length > 0 && (
                    <div className="resume-upload__footer">
                        <div className="resume-upload__footer-count">
                            {successFiles.length} {successFiles.length === 1 ? 'file' : 'files'} ready
                        </div>
                        <button
                            className="resume-upload__done"
                            type="button"
                            disabled={files.some(f => f.uploading)}
                            onClick={close}
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
